import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackEvent } from "utility/mixpanel";
import { ReportLocationState } from "./reopen";

/**
 * How the learner arrived at the report: straight after their assessment, or
 * reopened from the score panel. The same words mobile sends.
 */
export type ReportSource = "Reveal" | "Score panel";

/**
 * The one place that decides the report's Source: the score panel's link
 * marks the report it opens as reopened, and every other way in is the reveal.
 */
export const useReportSource = (): ReportSource =>
  (useLocation().state as ReportLocationState | null)?.reopened === true
    ? "Score panel"
    : "Reveal";

/** What a chart control did. */
export type ReportChartInteraction = "band_tap" | "domain_tap";

interface ReportJourneyOptions {
  /** False until the report has the learner's scores to show. */
  active: boolean;
  /** 0-based position of the step on screen. */
  stepIndex: number;
  /**
   * The report's steps as this learner has them, each keyed by its stable
   * analytics name. Read from a ref because the list is built further down
   * the render than a hook may be called.
   */
  steps: { readonly current: readonly { key: string }[] };
  responseId: string | null;
  source: ReportSource;
}

interface StepVisit {
  key: string;
  index: number;
  enteredAt: number;
  /** What was already counted on this visit, so a chart hovered twice counts once. */
  interactions: Set<string>;
}

interface ReportSession {
  /** Time the report's steps were on screen, banked as each visit ends. */
  onScreenMs: number;
  lastKey: string;
  lastIndex: number;
  reachedEnd: boolean;
  ended: boolean;
}

/**
 * Records the learner's way through the report in Mixpanel: each step they
 * enter and how long they stayed, which chart parts they asked about, and how
 * the report ended.
 *
 * A step is left when the learner moves on or back, hides the tab, or leaves
 * the report. Coming back to a hidden tab enters the step again, so every
 * "Report Step Exited" pairs with a "Report Step Viewed" and its duration is
 * time the step was actually on screen. A step reached while the tab is hidden
 * (the reveal moves on by itself) is entered when the learner comes back.
 * "Report Exited" fires once per report: on unmount, which the close button,
 * Done and choosing a goal all lead to, or when the page itself goes away. Its
 * total is the steps' time on screen, so a tab left in the background does
 * not count.
 */
export const useReportJourney = ({
  active,
  stepIndex,
  steps,
  responseId,
  source,
}: ReportJourneyOptions) => {
  const context = useRef({ responseId, source });
  context.current = { responseId, source };
  const visit = useRef<StepVisit | null>(null);
  const session = useRef<ReportSession | null>(null);

  const common = useCallback(
    () => ({
      Source: context.current.source,
      "Response Id": context.current.responseId,
    }),
    []
  );

  const enter = useCallback(
    (key: string, index: number) => {
      const current = session.current;
      if (!current || current.ended) return;
      current.lastKey = key;
      current.lastIndex = index;
      if (document.visibilityState === "hidden") return;
      const count = steps.current.length;
      visit.current = {
        key,
        index,
        enteredAt: Date.now(),
        interactions: new Set(),
      };
      if (index === count - 1) current.reachedEnd = true;
      trackEvent("Report Step Viewed", {
        Step: key,
        "Step Index": index,
        "Step Count": count,
        ...common(),
      });
    },
    [common, steps]
  );

  const exit = useCallback(() => {
    const left = visit.current;
    if (!left) return;
    visit.current = null;
    const duration = Date.now() - left.enteredAt;
    if (session.current) session.current.onScreenMs += duration;
    trackEvent("Report Step Exited", {
      Step: left.key,
      "Step Index": left.index,
      "Duration Ms": duration,
      ...common(),
    });
  }, [common]);

  const end = useCallback(() => {
    const current = session.current;
    if (!current || current.ended) return;
    exit();
    current.ended = true;
    trackEvent("Report Exited", {
      "Last Step": current.lastKey,
      "Last Step Index": current.lastIndex,
      Completed: current.reachedEnd,
      "Total Duration Ms": current.onScreenMs,
      ...common(),
    });
  }, [common, exit]);

  useEffect(() => {
    const key = steps.current[stepIndex]?.key;
    if (!active || key === undefined) return;
    if (!session.current) {
      session.current = {
        onScreenMs: 0,
        lastKey: key,
        lastIndex: stepIndex,
        reachedEnd: false,
        ended: false,
      };
      // Once per report, the same words mobile sends.
      if (context.current.source === "Score panel") {
        trackEvent("Report Reopened", common());
      }
    }
    exit();
    enter(key, stepIndex);
  }, [active, stepIndex, steps, enter, exit, common]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        exit();
        return;
      }
      const current = session.current;
      if (!visit.current && current && !current.ended) {
        enter(current.lastKey, current.lastIndex);
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", end);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", end);
    };
  }, [enter, exit, end]);

  useEffect(() => end, [end]);

  /** Counts a chart interaction once per step visit, per control and value. */
  const chartInteracted = useCallback(
    (interaction: ReportChartInteraction, value: string) => {
      const current = visit.current;
      if (!current) return;
      const id = `${interaction}:${value}`;
      if (current.interactions.has(id)) return;
      current.interactions.add(id);
      trackEvent("Report Chart Interacted", {
        Interaction: interaction,
        Value: value,
        Step: current.key,
        "Response Id": context.current.responseId,
      });
    },
    []
  );

  return { chartInteracted };
};
